import { Modal, notification } from "antd";
import cuid from "cuid";
import GeneralCosts from "entities/GeneralCosts";
import { find, get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import edit from "shared/assets/icons/generate.svg";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { SYNC_PROJECT_SELLIGENT } from "utils/api/graphql/mutations/projects";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  CONTACT_POINT_SYSTEM,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  PROCESS_TYPES,
  PROJECT_SOURCE,
  SELLIGENT_STEPS,
  TEMPLATE_TYPES,
  TEMPLATES,
  THEMES_PALETTE,
} from "utils/constants";
import { removeDuplicates } from "utils/helpers/array";
import View from "./View";

const Quote = ({ project, onNext, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [nextLoading, setNextLoading] = useState(false);
  const [syncProjectWithSelligent] = useMutation(SYNC_PROJECT_SELLIGENT, {
    onError: (error) => console.log(error),
  });
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [attachments, setAttachments] = useState([
    {
      name: DETAIL.TEMPLATES.QUOTE.QUOTE_FILE,
      contentType: "application/pdf",
    },
    {
      name: DETAIL.TEMPLATES.QUOTE.DUTY_FILE,
      contentType: "application/pdf",
    },
  ]);

  const { loading } = useQuery(graphql.queries.ATTACHMENTS, {
    variables: {
      isIn: { type: Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES) },
      where: {
        OR: get(project, "fields.lastSelected", [])?.map((id) => ({
          AND: [{ insurancePlans: { id } }, { project: null }],
        })),
      },
    },
    onCompleted: (data) => {
      const uniqAttachments = removeDuplicates(
        get(data, "attachments.data", []),
      );
      setAttachments((prev) => [...prev, ...uniqAttachments]);
    },
  });

  const [addLink] = useMutation(graphql.mutations.CREATE_LINK);
  const [getTracer] = useLazyQuery(TRACER);

  const [sendQuoteMutation] = useMutation(graphql.mutations.SEND_QUOTE, {
    onCompleted: (_) => {
      notification.open({
        message: DETAIL.TEMPLATES.QUOTE.NOTIFICATION.SUCCESS,
        duration: 5,
        type: "success",
      });
      onNext({
        payload: {
          locked: true,
          fields: {
            ...project.fields,
            insurancePlans: [
              ...get(project, "fields.insurancePlans", []),
              ...get(project, "fields.lastSelected", []),
            ],
          },
        },
        onCompleted: () => setNextLoading(false),
        onError: () => setNextLoading(false),
      });
    },
  });
  const [updateContact] = useMutation(graphql.mutations.UPDATE_CONTACT, {
    onCompleted: async () => {
      try {
        const {
          data: { tracer: tracerData },
        } = await getTracer({
          variables: {
            where: {
              flow: {
                process: {
                  theme: THEMES_PALETTE.B2B_RADIANCE_COSA.PROSPECT_PROCESS,
                },
              },
            },
          },
        });

        const selectedData = get(project, "fields.lastSelected", []);
        const insurancePlans = selectedData.map((id) => ({ id }));

        const { errors } = await generateQuoteDocuments({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlans,
            },
          },
          onCompleted: () => {
            if (
              project.externalId &&
              get(project, "fields.source") !== PROJECT_SOURCE.CLIENT
            )
              (async () => {
                for (const insurancePlanId of selectedData) {
                  syncProjectWithSelligent({
                    variables: {
                      project: { id: project.id },
                      insurancePlan: { id: insurancePlanId },
                      step: SELLIGENT_STEPS.PROPOSITION,
                    },
                  });
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }
              })();
          },
        });

        if (errors) {
          setNextLoading(false);
          throw new Error(`GraphQL Error: ${JSON.stringify(errors)}`);
        }

        let links = {};

        const createSubscription = async (insurancePlanId) => {
          const { data: subscriptionData } = await addSubscription({
            variables: {
              data: {
                id: cuid(),
                project: { id: project.id },
                insurancePlan: { id: insurancePlanId },
              },
            },
          });

          if (get(project, "fields.addLinks")) {
            const statuses = get(
              tracerData,
              "flow.process.processStatuses",
              [],
            );
            const selectedStatus = find(statuses, {
              template:
                TEMPLATES[PROCESS_TYPES.B2C].THEMES[
                  TEMPLATE_TYPES.RADIANCE_COSA
                ].B2C_SUBSCRIPTION,
            })?.id;

            const url = get(
              subscriptionData,
              "addSubscription.insurancePlan.config.generatedUrl",
              `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${get(
                tracerData,
                "source.id",
              )}`,
            );

            const { data: linkData } = await addLink({
              variables: {
                data: {
                  url,
                  subscription: { id: subscriptionData.addSubscription.id },
                  project: { id: project.id },
                  fields: {
                    process: { id: get(tracerData, "flow.process.id") },
                    status: { id: selectedStatus },
                  },
                },
              },
            });

            links[insurancePlanId] = `${url}?l=${linkData.addLink.id}`;
          }
        };

        await Promise.all(selectedData.map(createSubscription));

        await sendQuoteMutation({
          variables: {
            data: {
              links,
              project: { id: project.id },
              insurancePlans,
            },
          },
        });
        setNextLoading(false);
      } catch (error) {
        setNextLoading(false);
        console.error("Error in updateContact mutation:", error);
      }
    },
  });

  const sendQuote = (telecoms) => {
    setNextLoading(true);
    updateContact({
      variables: {
        where: {
          id: project.contact.id,
        },
        data: {
          telecoms: [
            ...project.contact.telecoms.filter(
              (telecom) => telecom.system !== CONTACT_POINT_SYSTEM.EMAIL,
            ),
            ...telecoms.map(({ label, value }) => ({
              id: value,
              system: CONTACT_POINT_SYSTEM.EMAIL,
              value: label,
            })),
          ],
        },
      },
    });
  };

  const generalCostsFilter = {
    where: {
      project: {
        id: project.id,
      },
    },
    isIn: {
      insurancePlan: {
        id: get(project, "fields.lastSelected", []),
      },
    },
  };

  return (
    <>
      {nextLoading && (
        <Modal open width={"60%"} footer={null}>
          <div className="modal-info">
            <img src={edit} alt={"en cours"} />
            <div className="modal-info__text">
              <h4>{DETAIL.TEMPLATES.QUOTE.IN_PROGRESS}</h4>
              <p>{DETAIL.TEMPLATES.QUOTE.GENERATING_DOCUMENTS}</p>
              <p>{DETAIL.TEMPLATES.QUOTE.FEW_SECONDS}</p>
            </div>
          </div>
        </Modal>
      )}
      <GeneralCosts
        filter={generalCostsFilter}
        loading={loading}
        updateContact={updateContact}
        attachments={attachments}
        setAttachments={setAttachments}
        project={project}
        onNext={sendQuote}
        nextLoading={nextLoading}
        onBack={onBack}
        View={View}
        NoData={View}
      />
    </>
  );
};

export default Quote;
